import { useState } from 'react';
import { Box } from '@mui/material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';

// Click-to-play "lite" YouTube embed: shows the poster thumbnail + a play button
// and only loads the heavy YouTube iframe once the user activates it. We require a
// click because YouTube playback uses sound (unlike the muted/autoplay background
// <video> used elsewhere in TextImageSlice).
//
// Renders as a clean, contained 16:9 player at the column's configured width (no
// black letterbox bars). Slimmed chrome via player params; YouTube no longer lets
// the wordmark/title or a channel watermark be removed from an iframe embed.
const YouTubeEmbed = ({ videoId, title = 'YouTube video', maxWidth, justify }) => {
    const [activated, setActivated] = useState(false);

    if (!videoId) return null;

    const poster = (
        <Box
            component="button"
            type="button"
            onClick={() => setActivated(true)}
            aria-label={`Spela upp video: ${title}`}
            sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                p: 0,
                border: 0,
                cursor: 'pointer',
                display: 'block',
                background: 'transparent',
                '&:hover .yt-play-btn': {
                    backgroundColor: '#FF0000',
                    transform: 'translate(-50%, -50%) scale(1.06)',
                },
            }}
        >
            <Box
                component="img"
                src={`https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`}
                onError={(e) => { e.currentTarget.src = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`; }}
                alt=""
                loading="lazy"
                sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
            <Box sx={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.12)' }} />
            <Box
                className="yt-play-btn"
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: 60, md: 76 },
                    height: { xs: 60, md: 76 },
                    borderRadius: '50%',
                    backgroundColor: 'rgba(18,18,18,0.78)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 6px 24px rgba(0,0,0,0.45)',
                    transition: 'background-color 0.2s ease, transform 0.2s ease',
                }}
            >
                <PlayArrowRoundedIcon sx={{ color: '#fff', fontSize: { xs: 38, md: 48 }, ml: '2px' }} />
            </Box>
        </Box>
    );

    const iframe = (
        <Box
            component="iframe"
            src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&playsinline=1&iv_load_policy=3&color=white`}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0, display: 'block' }}
        />
    );

    return (
        <Box sx={{ display: 'flex', justifyContent: justify, width: '100%' }}>
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    maxWidth,
                    aspectRatio: '16 / 9',
                    overflow: 'hidden',
                    backgroundColor: '#000',
                }}
            >
                {activated ? iframe : poster}
            </Box>
        </Box>
    );
};

export default YouTubeEmbed;
